'use client';

import classNames from 'classnames';
import logInstallPromptClicked from '@alltrails/amplitude/events/logInstallPromptClicked';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useDeeplink from '@alltrails/install-prompt/hooks/useDeeplink';
import styles from './AppInstallSectionStacked.module.scss';
import phoneMaskSrc from './images/phone-body.avif';
import QRCodeDownload from './QRCodeDownload';

type AppInstallData = {
  title: string;
  description: string;
  videoUrl: string;
};

const baseVideoUrl = `https://cdn-assets.alltrails.com/assets/videos/homepage-app-feature`;

const stackedContent: AppInstallData[] = [
  {
    title: 'Record your activites',
    description: 'Never miss a turn with live tracking. Relive every adventure with recaps and sharing.',
    videoUrl: `${baseVideoUrl}/navigator.mp4`
  },
  {
    title: 'Explore even without service',
    description: 'Download maps for trails and parks around the world.',
    videoUrl: `${baseVideoUrl}/offline.mp4`
  },
  {
    title: 'Create your own route',
    description: 'Build or edit the perfect trail for you.',
    videoUrl: `${baseVideoUrl}/byot.mp4`
  }
];

const AppInstallSectionStacked = () => {
  const getDeeplink = useDeeplink({});
  const isMobileSizedScreen = useIsMobileSizedScreen();

  const handleClick = () => {
    logInstallPromptClicked({
      install_prompt_location: 'home page',
      install_prompt_type: 'interstitial',
      install_prompt_source: 'native_with_appsflyer_link'
    });
    window.location.href = getDeeplink();
  };

  return (
    <section className={styles.outerContainer}>
      <div className={styles.container}>
        {stackedContent.map(({ title, description, videoUrl }, index) => {
          const alternateLayout = index === 1;

          return (
            <div key={index} className={classNames(styles.row, { [styles.rowReverse]: alternateLayout })}>
              <div className={classNames(styles.outerContent, { [styles.outerContentReverse]: alternateLayout })}>
                <div className={styles.content}>
                  <Heading as="h2" size="600">
                    {title}
                  </Heading>
                  <Typography variant="text300">{description}</Typography>
                  {!isMobileSizedScreen && <QRCodeDownload />}
                </div>
              </div>
              <div className={styles.phoneOuterContainer}>
                <div className={styles.phoneContainer}>
                  <img src={phoneMaskSrc.src} alt="" className={styles.phoneImg} />
                  <div className={styles.videoContainer}>
                    <video title={title} autoPlay loop muted playsInline className={styles.video}>
                      <source src={videoUrl} type="video/mp4" />
                    </video>
                  </div>
                </div>
                <div className={styles.bottomLine} />
              </div>
              {isMobileSizedScreen && <Button text="Download the app" variant="primary" onClick={handleClick} className={styles.button} />}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AppInstallSectionStacked;
